import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Send, Mic, Trash } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export type RecordingCompletePayload = {
  blob: Blob
  file: File
  mimeType: string
  durationSeconds: number
}

type Props = {
  disabled?: boolean
  autoStart?: boolean
  className?: string
  timerClassName?: string
  onRecordingComplete: (
    payload: RecordingCompletePayload
  ) => void | Promise<void>
  onRecordingError?: (error: Error) => void
  onCancel?: () => void
}

type StopAction = 'submit' | 'cancel'

const PREFERRED_AUDIO_MIME_TYPES = [
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
]

const AUDIO_EXTENSION_BY_MIME: Record<string, string> = {
  'audio/ogg;codecs=opus': 'ogg',
  'audio/ogg': 'ogg',
  'audio/webm;codecs=opus': 'webm',
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
}

function toError(
  error: unknown,
  fallbackMessage = 'Não foi possível acessar o microfone.'
): Error {
  if (error instanceof Error) {
    return error
  }

  if (typeof error === 'string' && error.trim()) {
    return new Error(error)
  }

  return new Error(fallbackMessage)
}

function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return undefined

  return PREFERRED_AUDIO_MIME_TYPES.find((mimeType) =>
    MediaRecorder.isTypeSupported(mimeType)
  )
}

function getFileExtension(mimeType: string) {
  if (AUDIO_EXTENSION_BY_MIME[mimeType]) {
    return AUDIO_EXTENSION_BY_MIME[mimeType]
  }

  const normalizedMimeType = mimeType.split(';')[0]
  if (AUDIO_EXTENSION_BY_MIME[normalizedMimeType]) {
    return AUDIO_EXTENSION_BY_MIME[normalizedMimeType]
  }

  const [, subtype = 'webm'] = normalizedMimeType.split('/')
  return subtype
}

const padWithLeadingZeros = (num: number, length: number): string => {
  return String(num).padStart(length, '0')
}

export const AudioRecorderWithVisualizer = ({
  disabled,
  autoStart = false,
  className,
  timerClassName,
  onRecordingComplete,
  onRecordingError,
  onCancel,
}: Props) => {
  const [isRecording, setIsRecording] = useState(false)
  const [timer, setTimer] = useState(0)
  const [isStopping, setIsStopping] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const recordingChunksRef = useRef<BlobPart[]>([])
  const animationFrameRef = useRef<number | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const mimeTypeRef = useRef<string>('audio/webm')
  const hasAutoStartedRef = useRef(false)
  const stopActionRef = useRef<StopAction>('submit')
  const isStartingRef = useRef(false)

  const clearCanvas = useCallback(() => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')
    if (!context) return

    context.clearRect(0, 0, canvas.width, canvas.height)
  }, [])

  const stopVisualization = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    clearCanvas()
  }, [clearCanvas])

  const releaseMediaResources = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.ondataavailable = null
      mediaRecorder.onstop = null
      mediaRecorder.onerror = null
      mediaRecorder.stop()
    }

    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null

    analyserRef.current?.disconnect()
    analyserRef.current = null

    if (audioContextRef.current) {
      void audioContextRef.current.close()
      audioContextRef.current = null
    }

    mediaRecorderRef.current = null
    stopVisualization()
  }, [stopVisualization])

  const handleError = useCallback(
    (error: unknown) => {
      const normalizedError = toError(error)
      setIsRecording(false)
      setIsStopping(false)
      setTimer(0)
      recordingChunksRef.current = []
      releaseMediaResources()
      onRecordingError?.(normalizedError)
    },
    [onRecordingError, releaseMediaResources]
  )

  const finalizeRecording = useCallback(async () => {
    const stopAction = stopActionRef.current
    const mimeType = mimeTypeRef.current
    const chunks = recordingChunksRef.current

    recordingChunksRef.current = []
    setIsRecording(false)
    setIsStopping(false)

    if (stopAction === 'cancel') {
      setTimer(0)
      releaseMediaResources()
      onCancel?.()
      return
    }

    if (!chunks.length) {
      setTimer(0)
      releaseMediaResources()
      handleError(new Error('Nenhum áudio foi capturado.'))
      return
    }

    try {
      const recordBlob = new Blob(chunks, { type: mimeType })
      const extension = getFileExtension(mimeType)
      const file = new File(
        [recordBlob],
        `voice-note-${Date.now()}.${extension}`,
        {
          type: mimeType,
        }
      )
      const durationSeconds = timer

      setTimer(0)
      await onRecordingComplete({
        blob: recordBlob,
        file,
        mimeType,
        durationSeconds,
      })
      releaseMediaResources()
    } catch (error) {
      setTimer(0)
      releaseMediaResources()
      handleError(error)
    }
  }, [handleError, onCancel, onRecordingComplete, releaseMediaResources, timer])

  const startRecording = useCallback(async () => {
    if (disabled || isRecording || isStopping || isStartingRef.current) return

    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      handleError(new Error('Seu navegador não suporta gravação de áudio.'))
      return
    }

    if (typeof MediaRecorder === 'undefined') {
      handleError(new Error('Seu navegador não suporta gravação de áudio.'))
      return
    }

    isStartingRef.current = true

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const audioContext = new AudioContext()
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048

      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyser)

      const mimeType = getSupportedMimeType()
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      mimeTypeRef.current = mediaRecorder.mimeType || mimeType || 'audio/webm'
      recordingChunksRef.current = []
      stopActionRef.current = 'submit'

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onerror = () => {
        handleError(new Error('Falha durante a gravação de áudio.'))
      }

      mediaRecorder.onstop = () => {
        void finalizeRecording()
      }

      mediaRecorderRef.current = mediaRecorder
      streamRef.current = stream
      analyserRef.current = analyser
      audioContextRef.current = audioContext

      mediaRecorder.start()
      setTimer(0)
      setIsRecording(true)
    } catch (error) {
      handleError(error)
    } finally {
      isStartingRef.current = false
    }
  }, [disabled, finalizeRecording, handleError, isRecording, isStopping])

  const stopRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current
    if (!mediaRecorder || mediaRecorder.state !== 'recording') return

    stopActionRef.current = 'submit'
    setIsStopping(true)
    mediaRecorder.stop()
  }, [])

  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current

    if (!mediaRecorder || mediaRecorder.state !== 'recording') {
      recordingChunksRef.current = []
      setTimer(0)
      setIsRecording(false)
      setIsStopping(false)
      releaseMediaResources()
      onCancel?.()
      return
    }

    stopActionRef.current = 'cancel'
    setIsStopping(true)
    mediaRecorder.stop()
  }, [onCancel, releaseMediaResources])

  useEffect(() => {
    if (!isRecording) return

    const timeoutId = window.setTimeout(() => {
      setTimer((prev) => prev + 1)
    }, 1000)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isRecording, timer])

  useEffect(() => {
    if (!isRecording || !canvasRef.current || !analyserRef.current) {
      stopVisualization()
      return
    }

    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    if (!context) return

    const width = canvas.width
    const height = canvas.height
    const analyser = analyserRef.current
    const bufferLength = analyser.fftSize
    const dataArray = new Uint8Array(bufferLength)

    const drawWaveform = () => {
      context.clearRect(0, 0, width, height)
      context.fillStyle = '#939393'

      const barWidth = 1
      const spacing = 1
      const maxBarHeight = height / 2.2
      const numBars = Math.floor(width / (barWidth + spacing))

      for (let i = 0; i < numBars; i++) {
        const sampleIndex = Math.floor((i / numBars) * bufferLength)
        const normalized = Math.abs((dataArray[sampleIndex] - 128) / 128)
        const barHeight = Math.max(2, Math.pow(normalized, 1.8) * maxBarHeight)
        const x = i * (barWidth + spacing)
        const y = height / 2 - barHeight / 2
        context.fillRect(x, y, barWidth, barHeight)
      }
    }

    const draw = () => {
      analyser.getByteTimeDomainData(dataArray)
      drawWaveform()
      animationFrameRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      stopVisualization()
    }
  }, [isRecording, stopVisualization])

  useEffect(() => {
    if (!autoStart || hasAutoStartedRef.current) return
    hasAutoStartedRef.current = true
    void startRecording()
  }, [autoStart, startRecording])

  useEffect(() => {
    return () => {
      releaseMediaResources()
    }
  }, [releaseMediaResources])

  const hours = Math.floor(timer / 3600)
  const minutes = Math.floor((timer % 3600) / 60)
  const seconds = timer % 60

  const [hourLeft, hourRight] = useMemo(
    () => padWithLeadingZeros(hours, 2).split(''),
    [hours]
  )
  const [minuteLeft, minuteRight] = useMemo(
    () => padWithLeadingZeros(minutes, 2).split(''),
    [minutes]
  )
  const [secondLeft, secondRight] = useMemo(
    () => padWithLeadingZeros(seconds, 2).split(''),
    [seconds]
  )

  const actionDisabled = disabled || isStopping

  return (
    <div
      className={cn(
        'relative flex h-16 w-full max-w-5xl items-center justify-center gap-2 rounded-md',
        {
          'border p-1': isRecording,
          'border-none p-0': !isRecording,
        },
        className
      )}
    >
      {isRecording ? (
        <Timer
          hourLeft={hourLeft}
          hourRight={hourRight}
          minuteLeft={minuteLeft}
          minuteRight={minuteRight}
          secondLeft={secondLeft}
          secondRight={secondRight}
          timerClassName={timerClassName}
        />
      ) : null}

      <canvas
        ref={canvasRef}
        className={cn('h-full w-full bg-background', !isRecording && 'hidden')}
      />

      <div className='flex gap-2'>
        {isRecording ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                onClick={cancelRecording}
                size='icon'
                variant='destructive'
                disabled={actionDisabled}
              >
                <Trash size={15} />
              </Button>
            </TooltipTrigger>
            <TooltipContent className='m-2'>
              <span>Descartar gravação</span>
            </TooltipContent>
          </Tooltip>
        ) : null}

        <Tooltip>
          <TooltipTrigger asChild>
            {!isRecording ? (
              <Button
                type='button'
                onClick={() => void startRecording()}
                size='icon'
                disabled={actionDisabled}
              >
                <Mic size={15} />
              </Button>
            ) : (
              <Button
                type='button'
                onClick={stopRecording}
                size='icon'
                disabled={actionDisabled}
              >
                <Send size={15} />
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent className='m-2'>
            <span>{!isRecording ? 'Iniciar gravação' : 'Enviar áudio'}</span>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

const Timer = ({
  hourLeft,
  hourRight,
  minuteLeft,
  minuteRight,
  secondLeft,
  secondRight,
  timerClassName,
}: {
  hourLeft: string
  hourRight: string
  minuteLeft: string
  minuteRight: string
  secondLeft: string
  secondRight: string
  timerClassName?: string
}) => {
  return (
    <div
      className={cn(
        'absolute -top-12 left-0 flex items-center justify-center gap-0.5 rounded-md border p-1.5 font-mono font-medium text-foreground',
        timerClassName
      )}
    >
      <span className='rounded-md bg-background p-0.5 text-foreground'>
        {hourLeft}
      </span>
      <span className='rounded-md bg-background p-0.5 text-foreground'>
        {hourRight}
      </span>
      <span>:</span>
      <span className='rounded-md bg-background p-0.5 text-foreground'>
        {minuteLeft}
      </span>
      <span className='rounded-md bg-background p-0.5 text-foreground'>
        {minuteRight}
      </span>
      <span>:</span>
      <span className='rounded-md bg-background p-0.5 text-foreground'>
        {secondLeft}
      </span>
      <span className='rounded-md bg-background p-0.5 text-foreground'>
        {secondRight}
      </span>
    </div>
  )
}
